import type { ReferenceDto } from 'cadenza/api-client/dto/reference-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';

/** @see net.disy.services.layer.timeline.web.dto.TimelinesDto */
export interface TimelinesDto {
  readonly timelines: TimelineDto[];
  readonly styles: TimelineStyleDto[];
  readonly layerSettings: TimelineLayerSettingsDto;
  readonly staticPoints: StaticPointsDto;
  readonly maximumDisplayPointCount: number;
  readonly warnPointCount: number;
  readonly totalPointCount: number;
  readonly layersWithTruncatedResult: LayerWithFeatureLimitDto[];
  readonly extent: WorldBoxDto;
  readonly timeScaleTicks: number[];
}

/** @see net.disy.services.layer.timeline.web.dto.TimelineDto */
export interface TimelineDto {
  readonly layerId: string;
  readonly timelineId: string;
  readonly layerName: string;
  readonly points: TimelinePointDto[];
  readonly visible: boolean;
  readonly linesVisible: boolean;
}

/** @see net.disy.services.layer.timeline.web.dto.TimelinePointDto */
export interface TimelinePointDto {
  readonly x: number;
  readonly y: number;
  readonly styleReference: ReferenceDto;
  readonly time: number;
  readonly featureId: string;
  readonly featureIdValues: string[];
}

/** @see net.disy.services.layer.timeline.web.dto.TimelineStyleDto */
export interface TimelineStyleDto {
  readonly [key: string]: object | number;
  readonly hashCode: number;
}

/** @see net.disy.services.layer.timeline.web.dto.TimelineLayerSettingsDto */
export interface TimelineLayerSettingsDto {
  readonly [key: string]: TimelineLayerSettingsEntryDto;
}

/** @see net.disy.services.layer.timeline.web.dto.TimelineLayerSettingsEntryDto */
export interface TimelineLayerSettingsEntryDto {
  readonly fading: TimelineLayerFadingSettingsDto;
  readonly highlightLastPoint?: boolean;
}

/** @see net.disy.services.layer.timeline.web.dto.TimelineLayerFadingSettingsDto */
export interface TimelineLayerFadingSettingsDto {
  readonly type: TimelineLayerFadingType;
  readonly transition: TimelineLayerFadingTransition;
  readonly duration: TimelineLayerFadingSettingsDurationDto;
  readonly count: number;
}

/** @see net.disy.services.layer.timeline.web.dto.TimelineLayerFadingSettingsDurationDto */
export interface TimelineLayerFadingSettingsDurationDto {
  readonly milliseconds: number;
  readonly configurationUnit: TimelineLayerFadingDurationUnit;
}

/** @see net.disy.services.layer.timeline.web.dto.StaticPointsDto */
export interface StaticPointsDto {
  readonly layerId: string;
  points: TimelinePointDto[];
}

/** @see net.disy.services.layer.timeline.web.dto.TimelinesDto.LayerWithFeatureLimitDto */
export interface LayerWithFeatureLimitDto {
  readonly id: string;
  readonly name: string;
  readonly limit: number;
}

/**
 * @see net.disy.gis.layer.configuration.atoms.content.timeline.IFading
 */
export const TimelineLayerFadingType = {
  NO_FADING: 'NoFading',
  AFTER_DURATION_FADING: 'AfterDurationFading',
  AFTER_COUNT_FADING: 'AfterCountFading'
} as const;

export type TimelineLayerFadingType = typeof TimelineLayerFadingType[keyof typeof TimelineLayerFadingType];

/**
 * @see net.disy.gis.layer.configuration.atoms.content.timeline.FadingTransition.java
 */
export const TimelineLayerFadingTransition = {
  NONE: 'NONE',
  TRANSPARENCY: 'TRANSPARENCY',
  SIZE: 'SIZE',
  TRANSPARENCY_AND_SIZE: 'TRANSPARENCY_AND_SIZE'
} as const;

export type TimelineLayerFadingTransition = keyof typeof TimelineLayerFadingTransition;

/**
 * A subset of `java.util.concurrent.TimeUnit`
 *
 * @see net.disy.gis.layer.configuration.atoms.content.timeline.AfterDurationFading
 */
export const TimelineLayerFadingDurationUnit = {
  MILLISECONDS: 'MILLISECONDS',
  SECONDS: 'SECONDS',
  MINUTES: 'MINUTES',
  HOURS: 'HOURS',
  DAYS: 'DAYS'
} as const;

export type TimelineLayerFadingDurationUnit = keyof typeof TimelineLayerFadingDurationUnit;
